import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import {
  reputationManagementData,
  emailMarketingData,
  directMailData,
} from 'components/Page/MarketingPro/data/marketingProDetailData';
import SEO, { createSEO } from 'components/Core/SEO';
import ContentfulAccordion from 'components/Contentful/ContentfulAccordion';
import { subNavData } from 'components/Page/MarketingPro/data/subNavData';
import useMarketingProDetailData from 'components/Page/MarketingPro/hooks/useMarketingProDetailData';
import WebsiteLayout from 'layouts/websiteLayout';
import SubNav from 'components/Page/MarketingPro/MarketingProSubNav';
import MarketingProHero from 'components/Page/MarketingPro/MarketingProHero';
import MarketingProOverviewVideo from 'components/Page/MarketingPro/MarketingProOverviewVideo';
import MarketingProRowContent from 'components/Page/MarketingPro/MarketingProRowContent';
import MarketingProListingsScan from 'components/Page/MarketingPro/MarketingProListingsScan';
import ContentfulRowContent from 'components/Contentful/ContentfulRowContent';
import useBreakpoint from 'utils/useBreakpoint';
import useGlobalHeader from 'hooks/useGlobalHeader';
import useGlobalFooter from 'hooks/useGlobalFooter';
import Breadcrumbs from 'components/Core/Breadcrumbs';
import generateBreadcrumbs from '../utils/getBreadcrumbData';

const MarketingProDetail = props => {
  const {
    pageContext: { slug },
  } = props;
  const [stickyHeader, setStickyHeader] = useState(false);
  const [stickyHiddenHeader, setStickyHiddenHeader] = useState(false);
  const {
    demoFormSection,
    directMailFaqs,
    emailFaqs,
    heroInlineMarketingForm,
    listingScanSocialIcons,
    reputationFaqs,
    videoBackground,
    yextListingsScanForm,
    emailSeo,
    mailSeo,
    reputationSeo,
  } = useMarketingProDetailData();
  let seo;
  const subNavForm = heroInlineMarketingForm?.onFormSubmissionModalMarketoForm;
  let heroVideo;
  let faqs;
  let copyImageSectionData;
  let bottomVideoData;
  const breakpoint = useBreakpoint();
  const isMobile =
    breakpoint?.breakpoint === 'xs' ||
    breakpoint?.breakpoint === 'sm' ||
    breakpoint?.breakpoint === 'md';
  const header = useGlobalHeader();
  const footer = useGlobalFooter();
  const formattedSlug = slug.charAt(0) !== '/' ? `/${slug}` : slug;
  if (formattedSlug === '/features/marketingpro/reputation') {
    faqs = reputationFaqs;
    copyImageSectionData = reputationManagementData();
    heroVideo = reputationManagementData().video;
    bottomVideoData = copyImageSectionData.bottomVideo;
    seo = reputationSeo;
  } else if (formattedSlug === '/features/marketingpro/email') {
    faqs = emailFaqs;
    copyImageSectionData = emailMarketingData();
    heroVideo = emailMarketingData().video;
    bottomVideoData = copyImageSectionData.bottomVideo;
    seo = emailSeo;
  } else if (formattedSlug === '/features/marketingpro/direct-mail') {
    faqs = directMailFaqs;
    copyImageSectionData = directMailData();
    heroVideo = directMailData().video;
    bottomVideoData = copyImageSectionData.bottomVideo;
    seo = mailSeo;
  }

  const stickyPositionRef = useRef();
  const pageSeo = createSEO(seo, props, faqs);
  const breadcrumbs = generateBreadcrumbs(
    seo,
    copyImageSectionData?.hero || { background: 'black' }
  );
  return (
    <Container stickyHiddenHeader={stickyHiddenHeader}>
      <WebsiteLayout
        header={header}
        footer={footer}
        stickyHeader={stickyHeader}
        theme="marketingPro"
        hero={false}
      >
        <SEO {...pageSeo} />
        <SubNav
          stickyPositionRef={stickyPositionRef}
          slug={formattedSlug}
          data={subNavData}
          onFormSubmissionModalMarketoForm={subNavForm}
          setStickyHeader={boolean => setStickyHeader(boolean)}
          setStickyHiddenHeader={boolean => setStickyHiddenHeader(boolean)}
        />
        {breadcrumbs?.data && (
          <Breadcrumbs
            breadcrumbs={breadcrumbs.data}
            absoluteMode={breadcrumbs.absolute}
            layout="marketingpro"
          />
        )}

        <MarketingProHero
          type="video-right"
          isMobile={isMobile}
          videoId={copyImageSectionData.videoId}
          video={heroVideo}
          form={heroInlineMarketingForm}
          {...copyImageSectionData.hero}
        />
        <div ref={stickyPositionRef} />
        {slug === 'reputation' && (
          <MarketingProListingsScan icons={listingScanSocialIcons} form={yextListingsScanForm} />
        )}
        {copyImageSectionData.copyImageSections.map((rowContent, index) => {
          return (
            <MarketingProRowContent
              key={`row${index}`}
              type="feature"
              backgroundColor="var(--dark-gray-color)"
              {...rowContent}
            />
          );
        })}
        <MarketingProOverviewVideo
          author={bottomVideoData.author}
          quote={bottomVideoData.quote}
          mainSrc={bottomVideoData.videoSrc}
          logo={bottomVideoData.logo}
          logoAlt={bottomVideoData.logoAlt}
          bgImage={videoBackground?.image?.file?.url}
        />
        <StyledFAQs
          background="var(--dark-gray-color)"
          accordionBgColor="var(--light-gray-color)"
          title={faqs.heading}
          faqs={faqs.components}
          border={false}
        />
        <RowContentCTA>
          <ContentfulRowContent {...demoFormSection} />
        </RowContentCTA>
      </WebsiteLayout>
    </Container>
  );
};

export default MarketingProDetail;

const Container = styled.div`
  .header-container {
    ${({ stickyHiddenHeader }) =>
      stickyHiddenHeader
        ? `position: fixed;
        top: -168px;
        transition: all 0.7s;
        width: 100%;
        z-index: 999;
      `
        : ''}
  }
  main {
    padding-top: 72px;
  }
  .website-header--inner-container {
    background: var(--white-color);
  }
  .breadcrumb--wrapper {
    top: 100px;
  }
`;

const StyledFAQs = styled(ContentfulAccordion)`
  .rc-collapse-content-box {
    p {
      color: #f8f8f8 !important;
    }
  }
`;

const RowContentCTA = styled.div`
  @media (max-width: 576px) {
    .row-content--col {
      min-height: 150px;
    }
  }
`;
